/**
 * MuiDashboard - Optimized Main Dashboard
 *
 * OPTIMIZATION: Phase 11.2 - Dashboard optimization
 *
 * Changes made:
 * - Replaced getCustomers(true) with fetchDashboardData() optimized function
 * - fetchDashboardData fetches only required fields (id, name, totals, ammount)
 * - Server-side pre-filtering of related data reduces payload size
 * - Added refresh button for manual data reload
 * - Data processing moved to api_helper for reusability
 *
 * Benefits:
 * - Reduced data transfer (only essential fields fetched)
 * - Faster initial load and refresh
 * - Pre-computed totals from API helper
 */
import React, { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Box,
  Grid,
  Typography,
  Skeleton,
  Chip,
  IconButton,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import ReactApexChart from "react-apexcharts"
import { fetchDashboardData } from "helpers/api_helper"

import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PeopleIcon from "@mui/icons-material/People"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import VisibilityIcon from "@mui/icons-material/Visibility"
import DashboardIcon from "@mui/icons-material/Dashboard"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import RefreshIcon from "@mui/icons-material/Refresh"

const MuiDashboard = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [customerTotals, setCustomerTotals] = useState([])
  const [totalSales, setTotalSales] = useState(0)
  const [totalIncome, setTotalIncome] = useState(0)
  const [loading, setLoading] = useState(true)

  document.title = t("Dashboard") + " | FlexFlow ERP"

  // ============================================================================
  // OPTIMIZED DATA FETCHING
  // Uses fetchDashboardData() which fetches only required fields
  // ============================================================================
  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const data = await fetchDashboardData()
      setCustomerTotals(data.customerTotals)
      setTotalSales(data.totalSales)
      setTotalIncome(data.totalIncome)
    } catch (error) {
      console.error("Error fetching dashboard data:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const formatCurrency = (value) => `${Number(value).toFixed(2)}€`
  const totalOrderCount = customerTotals.reduce((acc, c) => acc + c.orderCount, 0)
  const totalCustomerCount = customerTotals.length
  const outstandingBalance = totalSales - totalIncome

  const chartOptions = {
    chart: {
      type: "bar",
      stacked: false,
      toolbar: { show: false },
      fontFamily: "Inter, sans-serif",
    },
    xaxis: {
      categories: customerTotals.slice(0, 10).map((c) => c.name),
      labels: { rotate: -45, style: { fontSize: "10px" } },
    },
    yaxis: {
      labels: { formatter: (v) => formatCurrency(v), style: { fontSize: "10px" } },
    },
    plotOptions: {
      bar: { horizontal: false, columnWidth: "50%", borderRadius: 3 },
    },
    dataLabels: { enabled: false },
    colors: ["#3b82f6", "#f59e0b"],
    legend: { position: "top", fontSize: "11px" },
    tooltip: { y: { formatter: (v) => formatCurrency(v) } },
    grid: { borderColor: "#f3f4f6", strokeDashArray: 3 },
  }

  const chartData = [
    { name: t("Total Orders"), data: customerTotals.slice(0, 10).map((c) => c.totalOrders) },
    { name: t("Outstanding"), data: customerTotals.slice(0, 10).map((c) => c.totalCustomer) },
  ]

  const columns = [
    {
      field: "name",
      headerName: t("Customer"),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500} color="#111827" sx={{ fontSize: "0.8125rem" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "orderCount",
      headerName: t("Orders"),
      width: 70,
      align: "center",
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600, bgcolor: "#dbeafe", color: "#1d4ed8" }}
        />
      ),
    },
    {
      field: "totalOrders",
      headerName: t("Sales"),
      width: 100,
      align: "right",
      renderCell: (params) => (
        <Typography variant="body2" color="#111827" sx={{ fontSize: "0.8125rem" }}>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: "totalPayments",
      headerName: t("Paid"),
      width: 100,
      align: "right",
      renderCell: (params) => (
        <Typography variant="body2" color="#16a34a" sx={{ fontSize: "0.8125rem" }}>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: "totalCustomer",
      headerName: t("Balance"),
      width: 100,
      align: "right",
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            fontSize: "0.8125rem",
            color: params.value > 0 ? "#f59e0b" : params.value < 0 ? "#16a34a" : "#6b7280",
          }}
        >
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/customer-detail/${params.row.id}`)
          }}
          sx={{ color: "#3b82f6" }}
        >
          <VisibilityIcon sx={{ fontSize: 16 }} />
        </IconButton>
      ),
    },
  ]

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e5e7eb", px: 2, py: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: "#6b7280" }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <DashboardIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
            <Typography variant="body2" fontWeight={500} color="#111827">{t("Dashboard")}</Typography>
          </Box>
          <IconButton
            size="small"
            onClick={fetchData}
            disabled={loading}
            sx={{ color: "#6b7280" }}
            title={t("Refresh")}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* KPI Stats Row */}
      <Box sx={{ px: 2, py: 1, bgcolor: "#fff", borderBottom: "1px solid #e5e7eb" }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ p: 1, borderRadius: 1, bgcolor: "#dbeafe" }}>
                <AttachMoneyIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
              </Box>
              <Box>
                {loading ? (
                  <Skeleton width={80} height={24} />
                ) : (
                  <>
                    <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.7rem", textTransform: "uppercase" }}>
                      {t("Total Sales")}
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="#111827" sx={{ fontSize: "0.9375rem" }}>
                      {formatCurrency(totalSales)}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ p: 1, borderRadius: 1, bgcolor: "#dcfce7" }}>
                <AccountBalanceWalletIcon sx={{ fontSize: 18, color: "#16a34a" }} />
              </Box>
              <Box>
                {loading ? (
                  <Skeleton width={80} height={24} />
                ) : (
                  <>
                    <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.7rem", textTransform: "uppercase" }}>
                      {t("Income")}
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="#16a34a" sx={{ fontSize: "0.9375rem" }}>
                      {formatCurrency(totalIncome)}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ p: 1, borderRadius: 1, bgcolor: "#fef3c7" }}>
                <ShoppingCartIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
              </Box>
              <Box>
                {loading ? (
                  <Skeleton width={60} height={24} />
                ) : (
                  <>
                    <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.7rem", textTransform: "uppercase" }}>
                      {t("Orders")}
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="#111827" sx={{ fontSize: "0.9375rem" }}>
                      {totalOrderCount}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ p: 1, borderRadius: 1, bgcolor: "#f3e8ff" }}>
                <PeopleIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />
              </Box>
              <Box>
                {loading ? (
                  <Skeleton width={40} height={24} />
                ) : (
                  <>
                    <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.7rem", textTransform: "uppercase" }}>
                      {t("Customers")}
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="#111827" sx={{ fontSize: "0.9375rem" }}>
                      {totalCustomerCount}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Outstanding Balance Highlight */}
      <Box sx={{ px: 2, py: 1, bgcolor: outstandingBalance > 0 ? "#fffbeb" : "#f0fdf4", borderBottom: "1px solid #e5e7eb" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="body2" color="#6b7280" sx={{ fontSize: "0.8125rem" }}>
            {t("Outstanding Balance")}
          </Typography>
          <Typography
            variant="body1"
            fontWeight={700}
            sx={{ fontSize: "1rem", color: outstandingBalance > 0 ? "#f59e0b" : "#16a34a" }}
          >
            {formatCurrency(outstandingBalance)}
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={1}>
          {/* Chart */}
          <Grid item xs={12} lg={6}>
            <Box sx={{ bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1, p: 1.5, height: "100%" }}>
              <Typography variant="body2" fontWeight={600} color="#111827" sx={{ mb: 1 }}>
                {t("Top 10 Customers")}
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
              ) : (
                <ReactApexChart options={chartOptions} series={chartData} type="bar" height={280} />
              )}
            </Box>
          </Grid>

          {/* Customer Table */}
          <Grid item xs={12} lg={6}>
            <Box sx={{ bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1, overflow: "hidden", height: "100%" }}>
              <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #f3f4f6" }}>
                <Typography variant="body2" fontWeight={600} color="#111827">
                  {t("Customer Summary")}
                </Typography>
                <Typography variant="caption" color="#6b7280">
                  {customerTotals.length} {t("customers with shipped orders")}
                </Typography>
              </Box>
              <DataGrid
                rows={customerTotals}
                columns={columns}
                onRowClick={(params) => navigate(`/customer-detail/${params.row.id}`)}
                disableRowSelectionOnClick
                disableColumnMenu
                density="compact"
                rowHeight={40}
                hideFooter={customerTotals.length <= 10}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[10, 25]}
                loading={loading}
                sx={{
                  border: "none",
                  height: 320,
                  "& .MuiDataGrid-columnHeaders": {
                    bgcolor: "#f9fafb",
                    borderBottom: "1px solid #e5e7eb",
                    minHeight: "36px !important",
                    maxHeight: "36px !important",
                  },
                  "& .MuiDataGrid-columnHeader": { height: "36px !important" },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    color: "#6b7280",
                    textTransform: "uppercase",
                  },
                  "& .MuiDataGrid-columnSeparator": { display: "none" },
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#e5e7eb" },
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #f3f4f6",
                    "&:focus, &:focus-within": { outline: "none" },
                  },
                  "& .MuiDataGrid-footerContainer": { minHeight: 36 },
                }}
                localeText={{ noRowsLabel: t("No data") }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default MuiDashboard
